import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Bed, Bath, Maximize, ArrowLeft, ArrowRight, Phone, MessageCircle } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

import { Navbar } from '@/components/Navbar';
import { formatPrice } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

import markerIconGreen from '../assets/images/marker-icon-green.png';
import markerShadow from '../assets/images/marker-shadow.png';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIconGreen,
  iconUrl: markerIconGreen,
  shadowUrl: markerShadow,
});

// Custom marker icon for properties
const propertyIcon = new L.Icon({
  iconUrl: markerIconGreen,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchProperty = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      setProperty(response.data);
    } catch (error) {
      console.error('Error fetching property:', error);
      toast.error('Property not found');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchProperty();
  }, [id, fetchProperty]);

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        property_id: property.property_id
      });
      toast.success('Inquiry submitted successfully! We\'ll contact you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsAppInquiry = () => {
    const message = `Hi, I'm interested in the property: ${property.title} (${property.location}, ${property.city}). Please provide more details.`;
    window.open(`https://wa.me/919715472526?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!property) return null;

  const defaultPosition = [11.0168, 76.9558]; // Coimbatore coordinates as default
  const position = property.latitude && property.longitude
    ? [property.latitude, property.longitude]
    : defaultPosition;

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 md:px-8 bg-stone-50">
      <Navbar glass={true} />
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Button
          data-testid="back-button"
          onClick={() => navigate('/properties')}
          variant="ghost"
          className="mb-8 text-primary hover:bg-primary/10 pl-0 hover:pl-2 transition-all"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Properties
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Content Column */}
          <div className="lg:col-span-8 space-y-8">

            {/* Apple-Style Image Slideshow */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-[2.5rem] p-4 shadow-xl"
            >
              <div className="relative h-[350px] md:h-[450px] rounded-[2rem] overflow-hidden group">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={selectedImage}
                    src={property.images[selectedImage] || 'https://images.unsplash.com/photo-1663756915301-2ba688e078cf?crop=entropy&cs=srgb&fm=jpg&q=85'}
                    alt={property.title}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="w-full h-full object-cover"
                  />
                </AnimatePresence>

                {/* Navigation Buttons */}
                {property.images.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImage((prev) => (prev === 0 ? property.images.length - 1 : prev - 1));
                      }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/25 transition-all opacity-0 group-hover:opacity-100"
                    >
                      <ArrowLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImage((prev) => (prev === property.images.length - 1 ? 0 : prev + 1));
                      }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/25 transition-all opacity-0 group-hover:opacity-100"
                    >
                      <ArrowRight className="w-6 h-6" />
                    </button>

                    {/* Image Counter */}
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/50 backdrop-blur-md rounded-full text-white text-sm font-medium border border-white/10">
                      {selectedImage + 1} / {property.images.length}
                    </div>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              {property.images.length > 1 && (
                <div className="mt-4 flex gap-3 overflow-x-auto pb-2 px-2 snap-x">
                  {property.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`relative flex-shrink-0 h-24 w-32 rounded-2xl overflow-hidden transition-all ${selectedImage === idx
                        ? 'ring-2 ring-primary ring-offset-2 scale-95'
                        : 'opacity-70 hover:opacity-100'
                        }`}
                    >
                      <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Property Details Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-[#FFFBF5] p-8 md:p-12 rounded-[2.5rem] shadow-xl border border-stone-100"
            >
              <h1 className="font-heading text-4xl md:text-5xl font-medium text-primary mb-4">{property.title}</h1>
              <div className="flex items-center gap-2 text-[#57534E] mb-8 text-lg">
                <MapPin className="w-5 h-5" />
                <span>{property.location}, {property.city}, {property.state}</span>
              </div>

              <div className="flex flex-wrap items-center gap-8 mb-8 pb-8 border-b border-stone-200/60">
                {property.bedrooms && (
                  <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow-sm border border-stone-100">
                    <Bed className="w-5 h-5 text-primary" />
                    <span className="font-medium text-[#57534E]">{property.bedrooms} Beds</span>
                  </div>
                )}
                {property.bathrooms && (
                  <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow-sm border border-stone-100">
                    <Bath className="w-5 h-5 text-primary" />
                    <span className="font-medium text-[#57534E]">{property.bathrooms} Baths</span>
                  </div>
                )}
                <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow-sm border border-stone-100">
                  <Maximize className="w-5 h-5 text-primary" />
                  <span className="font-medium text-[#57534E]">{property.area_sqft} sqft</span>
                </div>
              </div>

              <div className="mb-8">
                <span className="text-4xl md:text-5xl font-heading font-medium text-primary block mb-2">
                  {formatPrice(property.price)}
                </span>
                <span className="text-[#57534E] text-lg">Estimated Price</span>
              </div>

              <div className="space-y-8">
                <div>
                  <h3 className="font-heading text-2xl font-medium text-primary mb-4">Description</h3>
                  <p className="text-[#57534E] text-lg leading-relaxed">{property.description}</p>
                </div>

                {property.amenities && property.amenities.length > 0 && (
                  <div>
                    <h3 className="font-heading text-2xl font-medium text-primary mb-4">Amenities</h3>
                    <div className="flex flex-wrap gap-3">
                      {property.amenities.map((amenity, idx) => (
                        <span key={idx} className="px-6 py-3 bg-white border border-stone-100 text-primary rounded-xl text-md font-medium shadow-sm">
                          {amenity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Location Map */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white p-4 rounded-[2.5rem] shadow-xl"
            >
              <h2 className="font-heading text-2xl font-medium text-primary mb-6 px-4 pt-2">Location</h2>
              <div className="rounded-[2rem] overflow-hidden border border-stone-100" style={{ height: '400px' }}>
                <MapContainer center={position} zoom={13} style={{ height: '100%', width: '100%' }}>
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={position} icon={propertyIcon}>
                    <Popup>{property.title}</Popup>
                  </Marker>
                </MapContainer>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Inquiry Form (Sticky) */}
          <div className="lg:col-span-4">
            <div className="sticky top-28 space-y-6">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white p-8 rounded-[2.5rem] shadow-xl border border-stone-100"
              >
                <div className="text-center mb-8">
                  <h2 className="font-heading text-2xl font-medium text-primary mb-2">Interested?</h2>
                  <p className="text-[#57534E]">Book a visit or request more details</p>
                </div>

                <Button
                  data-testid="whatsapp-inquiry-button"
                  onClick={handleWhatsAppInquiry}
                  className="w-full mb-8 bg-[#25D366] hover:bg-[#20BD5A] text-white h-14 rounded-2xl font-medium text-lg shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                >
                  <MessageCircle className="w-6 h-6 mr-2" />
                  Chat on WhatsApp
                </Button>

                <div className="relative mb-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-stone-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-[#57534E] font-medium">OR EMAIL US</span>
                  </div>
                </div>

                <form onSubmit={handleInquirySubmit} className="space-y-4">
                  <Input
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="h-14 bg-stone-50 border-transparent focus:bg-white focus:border-primary/20 rounded-2xl px-6 text-lg"
                  />
                  <Input
                    type="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-14 bg-stone-50 border-transparent focus:bg-white focus:border-primary/20 rounded-2xl px-6 text-lg"
                  />
                  <Input
                    type="tel"
                    placeholder="Your Phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="h-14 bg-stone-50 border-transparent focus:bg-white focus:border-primary/20 rounded-2xl px-6 text-lg"
                  />
                  <Textarea
                    placeholder="I am interested in this property..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={4}
                    className="bg-stone-50 border-transparent focus:bg-white focus:border-primary/20 rounded-2xl px-6 py-4 text-lg resize-none"
                  />
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-14 bg-primary text-white hover:bg-primary/90 rounded-2xl font-medium text-lg shadow-lg"
                  >
                    {submitting ? 'Sending...' : 'Send Inquiry'}
                  </Button>
                </form>

                <div className="mt-8 pt-6 border-t border-stone-100 text-center">
                  <p className="text-sm text-[#57534E] mb-3">Direct Call Support</p>
                  <div className="flex flex-col gap-2 items-center">
                    <a href="tel:+919715472526" className="flex items-center gap-2 text-primary font-bold hover:text-accent transition-colors">
                      <Phone className="w-5 h-5" />
                      +91 97154 72526
                    </a>
                    <a href="tel:+916380803026" className="flex items-center gap-2 text-primary font-bold hover:text-accent transition-colors">
                      <Phone className="w-5 h-5" />
                      +91 63808 03026
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}